import { ILegalClient } from '../Interfaces/ILegalClient';
import {
  ClientWarning,
  canAccessPortal,
  hasEmail,
  portalBlockReason,
} from '../helper/clientEmailStatus';

/** Kann die Portal-Aufforderung bei diesem Beteiligten ankommen und bearbeitet
 *  werden? `null` heißt: alles in Ordnung.
 *
 *  Die QS wollte in der Übersicht der potentiellen Mandanten „eine Angabe der
 *  Mailadresse" sehen (qs/dino-qs#6). Die eigentlich handlungsleitende Angabe
 *  ist aber nicht die Adresse selbst, sondern **ob die Aufforderung ankommt**:
 *  sie geht per E-Mail, und eingereicht wird im Mandantenportal. Fehlt eines
 *  von beidem, läuft die Anforderung ins Leere — bisher ohne jeden Hinweis.
 *
 *  Dieselbe Klassifikation wie die Warnleisten bei den Zahlungsschuldnern und
 *  in der Mandanten-Übersicht (`helper/clientEmailStatus`), nur mit der Wortwahl
 *  dieses Bereichs.
 */
export const portalRequestReachability = (
  client: ILegalClient
): ClientWarning | null => {
  if (!hasEmail(client)) {
    return {
      severity: 'error',
      text: 'Keine E-Mail-Adresse hinterlegt — die Aufforderung kann nicht zugestellt werden.',
    };
  }

  if (!canAccessPortal(client)) {
    return {
      severity: 'error',
      text: `Kein Zugang zum Mandantenportal (${portalBlockReason(client)}) — der Beteiligte kann dort nichts einreichen.`,
    };
  }

  if (!client.isReceivingEmailEnabled) {
    return {
      severity: 'warning',
      text: 'Empfang von Benachrichtigungen ist deaktiviert — die Aufforderung wird nicht verschickt.',
    };
  }

  return null;
};

/** Teilt die angehakten Beteiligten in solche, bei denen die Aufforderung
 *  ankommt, und solche, bei denen sie ins Leere läuft.
 *
 *  Gebraucht für eine ehrliche Rückmeldung: die Erfolgsmeldung nannte bisher
 *  die Zahl der angehakten Beteiligten, auch wenn für keinen von ihnen eine
 *  Mail rausgehen konnte.
 */
export const splitPortalRequestRecipients = (
  participants: ILegalClient[],
  selectedIds: Set<number>
): { reachable: ILegalClient[]; unreachable: ILegalClient[] } => {
  const selected = participants.filter(client =>
    selectedIds.has(client.idLegalClient)
  );

  return {
    reachable: selected.filter(
      client => portalRequestReachability(client) === null
    ),
    unreachable: selected.filter(
      client => portalRequestReachability(client) !== null
    ),
  };
};
